/**
 * RFC4180 準拠の簡易 CSV パーサ／フォーマッタ。
 *
 * - ダブルクォートで囲まれたフィールドは中にカンマ/改行を含められる
 * - フィールド内のダブルクォートは `""` にエスケープする（RFC4180）
 */

/**
 * CSV テキストを 2 次元配列にパースする。改行コードは LF/CRLF 両方対応。
 * 空行（クォート外での完全に空の行）はスキップする。
 */
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let i = 0;

  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === ',') {
      row.push(field);
      field = '';
      i++;
      continue;
    }
    if (ch === '\n' || ch === '\r') {
      // CRLF を 1 つの改行として扱う
      if (ch === '\r' && text[i + 1] === '\n') {
        i++;
      }
      row.push(field);
      field = '';
      // 全フィールドが空の行はスキップ
      if (!(row.length === 1 && row[0] === '')) {
        rows.push(row);
      }
      row = [];
      i++;
      continue;
    }
    field += ch;
    i++;
  }

  // 最後のフィールド
  if (field !== '' || row.length > 0) {
    row.push(field);
    if (!(row.length === 1 && row[0] === '')) {
      rows.push(row);
    }
  }

  return rows;
}

/**
 * RFC4180 に従いフィールドをエスケープ（必要ならダブルクォートで囲む）する。
 */
export function escapeCsvField(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  const str = String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * 複数所属などの配列値を CSV 1 フィールドに変換する（常にダブルクォートで囲む）。
 */
export function stringifyMultiValue(values: string[] | undefined | null, sep = ','): string {
  const vs = (values || []).map((v) => String(v).trim()).filter(Boolean);
  const joined = vs.join(sep);
  // 常にダブルクォートで囲む（空でも空文字列を返す）
  if (!joined) return '';
  return `"${joined.replace(/"/g, '""')}"`;
}

/**
 * CSV の 1 フィールドからカンマ区切りの複数値を取り出す。
 */
export function parseMultiValue(raw: string | undefined | null, sep = ','): string[] {
  if (raw === undefined || raw === null) return [];
  return String(raw)
    .split(sep)
    .map((v) => v.trim())
    .filter(Boolean);
}

/**
 * 2次元配列を CSV 文字列に変換する（各フィールドは必要に応じて RFC4180 エスケープ）。
 */
export function serializeCsv(rows: (string | number | undefined | null)[][], eol = '\n'): string {
  return rows.map((r) => r.map((c) => escapeCsvField(c)).join(',')).join(eol);
}
